import { useEffect, useMemo, useState } from 'preact/hooks'
import { useSearchspringContext } from 'libs/searchspring/fetchers/provider'
import { SearchspringCollection } from 'libs/searchspring/fetchers/collection'
import { SearchspringSearch } from 'libs/searchspring/fetchers/search'
import { normaliseResults, requiresPriceLocalisation } from 'libs/searchspring/helpers/normalisers'
import { isPriceFilterField } from 'libs/searchspring/components/collection/filters/FilterUtils'

const resolvedBounds = new Map()

/**
 * Resolve real market prices for the outer bounds of a price facet.
 *
 * Only products that have actually loaded carry a known market price, so on a
 * long collection the cheapest and dearest items sit pages away. Asking the
 * index for those two items — one result each, sorted by price — and running
 * them through the usual normalisation records their real prices.
 */
export function usePriceBounds(field) {
  const { results, options } = useSearchspringContext()
  const [, setRecorded] = useState(0)

  const sortField = useMemo(() => {
    const sortOptions = results?.sorting?.options ?? []
    return sortOptions.find((option) => isPriceFilterField(option.field))?.field ?? null
  }, [results?.sorting])

  const handle = options?.get('handle')
  const query = options?.get('query')

  useEffect(() => {
    if (!isPriceFilterField(field) || !sortField) return
    if (!handle && !query) return
    if (!requiresPriceLocalisation()) return

    let active = true

    recordExtremePrices({ sortField, handle, query }).then(() => {
      if (active) setRecorded((count) => count + 1)
    })

    return () => {
      active = false
    }
  }, [field, sortField, handle, query])
}

/** One lookup per context, however many slider instances ask for it. */
function recordExtremePrices({ sortField, handle, query }) {
  const key = [handle ?? query, sortField].join(':')

  if (!resolvedBounds.has(key)) {
    resolvedBounds.set(key, fetchExtremePrices({ sortField, handle, query }))
  }

  return resolvedBounds.get(key)
}

async function fetchExtremePrices({ sortField, handle, query }) {
  await Promise.all(
    ['asc', 'desc'].map(async (direction) => {
      try {
        const fetcher = handle ? new SearchspringCollection() : new SearchspringSearch()
        fetcher.updateParams({ handle, query, resultsPerPage: 1, sort: `${sortField}:${direction}` })
        const data = await fetcher.fetch()
        await normaliseResults(data?.results)
      } catch (error) {
        console.error(
          `Could not resolve the ${direction === 'asc' ? 'lowest' : 'highest'} price for the price filter:`,
          error
        )
      }
    })
  )
}
